import angularModule from 'Lessons/angularModule/scripts/lessons_module';
import 'ExtensionMethods/array';
// Import directly from sync module to avoid loading hooks which have NavigationSectionKey dependency
import { appHeaderSynchronizer } from 'AppHeaderReact/sync';

angularModule.factory('Lesson.PlayerViewModel', [
    '$injector',

    $injector => {
        const SuperModel = $injector.get('SuperModel');
        const guid = $injector.get('guid');
        const reactRouterBridge = $injector.get('reactRouterBridge');
        const EventLogger = $injector.get('EventLogger');
        const Event = $injector.get('EventLogger.Event');

        return SuperModel.subclass(function () {
            this.defineCallbacks('started');
            this.defineCallbacks('completed');
            this.defineCallbacks('initialized');
            this.defineCallbacks('destroyed');
            this.defineCallbacks('linked_to_app_header_view_model');
            this.defineCallbacks('clear_client_storage');

            Object.defineProperty(this.prototype, 'started', {
                get() {
                    return this._started || false;
                },
                set(value) {
                    const self = this;
                    if (value === self.started) {
                        return;
                    }

                    if (value) {
                        self.runCallbacks('started', () => {
                            self._started = value;
                        });
                    } else {
                        self._started = value;
                    }
                },
            });

            Object.defineProperty(this.prototype, 'completed', {
                get() {
                    return this._completed || false;
                },
                set(value) {
                    const self = this;
                    if (!value && self.completed) {
                        throw new Error('player has already been marked as completed');
                    } else if (!value) {
                        return;
                    } else if (self.completed) {
                        return;
                    }

                    if (!self.started) {
                        throw new Error('player cannot be completed as it has not been marked as started');
                    }

                    if (value) {
                        self.runCallbacks('completed', () => {
                            self._completed = value;
                        });
                    } else {
                        self._completed = value;
                    }
                },
            });

            Object.defineProperty(this.prototype, 'previewMode', {
                get() {
                    return this.$$previewMode;
                },
                set(val) {
                    if (val === this.$$previewMode) {
                        return;
                    }

                    // setting the value to null will remove it entirely
                    // from the query parameters, which ensures it does
                    // not get set in the player
                    reactRouterBridge.search('preview', val ? 'y' : null);
                    this.$$previewMode = val;
                    if (val) {
                        this.editorMode = false;
                    }
                },
            });

            Object.defineProperty(this.prototype, 'regularPlayerMode', {
                get() {
                    return !this.editorMode && !this.previewMode;
                },
            });

            Object.defineProperty(this.prototype, 'lesson', {
                get() {
                    throw new Error('PlayerViewModel does not support lesson property by default.');
                },
                set() {
                    throw new Error('PlayerViewModel does not support lesson property by default.');
                },
            });

            Object.defineProperty(this.prototype, 'sessionId', {
                get() {
                    throw new Error('PlayerViewModel requires sessionId be defined by a mixin.');
                },
                configurable: true, // specs
            });

            Object.defineProperty(this.prototype, 'canNavigateFreely', {
                get() {
                    throw new Error('PlayerViewModel canNavigateFreely sessionId be defined by a mixin.');
                },
                configurable: true, // specs
            });

            Object.defineProperty(this.prototype, 'canNavigateBackToCompletedFrames', {
                get() {
                    throw new Error(
                        'PlayerViewModel canNavigateBackToCompletedFrames sessionId be defined by a mixin.',
                    );
                },
                configurable: true, // specs
            });

            Object.defineProperty(this.prototype, 'sessionType', {
                get() {
                    if (this.editorMode || this.previewMode) {
                        return 'lessonEditor';
                    }
                    return 'lessonPlayer';
                },
            });

            return {
                initialize(lesson, options) {
                    this.runCallbacks('initialized', function () {
                        options = options || {};
                        if (lesson) {
                            this.lesson = lesson;
                        }
                        this.id = guid.generate();
                        this.destroyed = false;
                        this.editorMode = options.editorMode === true;
                        this.demoMode = options.demoMode === true;

                        // oxlint-disable-next-line no-prototype-builtins
                        if (options.hasOwnProperty('previewMode')) {
                            this.previewMode = options.previewMode;
                        } else if (reactRouterBridge.search().preview === 'y') {
                            this.previewMode = true;
                        } else {
                            this.previewMode = false;
                        }

                        this.appHeaderViewModels = [];
                        this.messagingServices = [];

                        this.options = options;
                    });
                },

                destroy() {
                    if (!this.destroyed) {
                        this.runCallbacks('destroyed', function () {
                            this.delinkFromWindows();
                            this.destroyed = true;
                        });
                    }
                },

                // In previewMode, the same playerViewModel can be linked to
                // multiple windows (each in different iframes)
                // oxlint-disable-next-line no-shadow
                linkToWindow($injector) {
                    this._linkToAppHeaderViewModel($injector.get('Navigation.AppHeader.AppHeaderViewModel'));
                    this._linkToLessonMessagingService($injector.get('Lesson.MessagingService'));
                },

                // oxlint-disable-next-line no-shadow
                delinkFromWindow($injector) {
                    this._delinkFromAppHeaderViewModel($injector.get('Navigation.AppHeader.AppHeaderViewModel'));
                    this._delinkFromMessagingService($injector.get('Lesson.MessagingService'));
                },

                delinkFromWindows() {
                    this.appHeaderViewModels.forEach(appHeaderViewModel => {
                        this._delinkFromAppHeaderViewModel(appHeaderViewModel);
                    });

                    this.messagingServices = [];
                },

                setBodyBackground(color) {
                    this.appHeaderViewModels.forEach(appHeaderViewModel => {
                        appHeaderViewModel.setBodyBackground(color);
                    });
                },

                logInfo() {
                    const obj = {};
                    if (this.sessionType === 'lessonEditor') {
                        obj.lesson_editor_session_id = this.sessionId;
                    } else {
                        obj.lesson_player_session_id = this.sessionId;
                    }

                    return angular.extend(obj, {
                        // we still call this a lesson_play_id, even though it's the id of the player
                        // and not all players have lessons anymore.  Just for backwards consistency
                        lesson_play_id: this.id,
                        editorMode: this.editorMode,
                        previewMode: this.previewMode,
                        demoMode: this.demoMode, // note that this was not set for the first month of demos, from 10/16 - 11/20ish
                    });
                },

                showMessage(messageInfo) {
                    this.messagingServices.forEach(service => {
                        service.showMessage(messageInfo);
                    });
                },

                clearMessage() {
                    this.messagingServices.forEach(service => {
                        service.clearMessage();
                    });
                },

                _linkToAppHeaderViewModel(appHeaderViewModel) {
                    this.runCallbacks('linked_to_app_header_view_model', function () {
                        this.appHeaderViewModels.push(appHeaderViewModel);
                        appHeaderViewModel.playerViewModel = this;
                        try {
                            this.syncPlayerViewModelToRedux();
                        } catch (e) {
                            // Non-critical: Redux sync failure should not break lesson player
                            console.warn('Failed to sync player view model to Redux:', e);
                        }
                    });
                },

                _delinkFromAppHeaderViewModel(appHeaderViewModel) {
                    appHeaderViewModel.textRows = undefined;
                    appHeaderViewModel.playerViewModel = undefined;
                    Array.remove(this.appHeaderViewModels, appHeaderViewModel);
                    try {
                        appHeaderSynchronizer.syncPlayerViewModel(null);
                    } catch (e) {
                        console.warn('Failed to clear player view model in Redux:', e);
                    }
                },

                /**
                 * Extract player view model data for Redux sync.
                 * Called from _linkToAppHeaderViewModel and when properties change.
                 */
                // oxlint-disable-next-line complexity
                getPlayerViewModelDataForSync() {
                    // Use try-catch because base PlayerViewModel throws when accessing lesson property
                    // Subclasses (like FrameListPlayerViewModel) override this property
                    let lesson = null;
                    let stream = null;
                    try {
                        lesson = this.lesson;
                        stream = this.stream;
                    } catch {
                        // Base PlayerViewModel throws when lesson/stream are accessed
                    }

                    // Extract lesson editor-specific data
                    const versionHistory = lesson?.version_history || [];
                    const lastVersion = versionHistory[0];
                    const hasVersionHistory = versionHistory.length > 0;

                    return {
                        hasLesson: !!lesson,
                        showStartScreen: !!this.showStartScreen,
                        showFinishScreen: !!this.showFinishScreen,
                        demoMode: !!this.demoMode,
                        previewMode: !!this.previewMode,
                        editorMode: !!this.editorMode,
                        shouldShowPreviousButton: !!this.shouldShowPreviousButton,
                        frameInstructions: this._computeFrameInstructions(),
                        showFrameInstructions: true,
                        lessonTitle: lesson?.title || '',
                        lessonIsTest: !!lesson?.test,
                        lessonIsAssessment: !!lesson?.assessment,
                        lessonLocale: lesson?.locale || 'en',
                        lessonLocaleDirection: lesson?.locale_direction || 'ltr',
                        chapterIndex: lesson?.chapter()?.index ?? null,
                        textRows: this.textRows || [],
                        framesCount: this.frames?.length || 0,
                        swappingLanguage: !!this.swappingLanguage,
                        supportsLanguageSwap: !!this.supportsLanguageSwap,
                        streamDashboardPath: stream?.streamDashboardPath || null,
                        // Lesson editor-specific properties
                        lessonWasPublished: !!lesson?.was_published,
                        lessonUnrestricted: !!lesson?.unrestricted,
                        lessonPinnedTitle: lesson?.pinned_title || null,
                        lessonOldVersion: !!lesson?.old_version,
                        lessonEditorName: lesson?.editorName || null,
                        lessonUpdatedAtFormatted: lesson?.formatDate?.(lesson?.updated_at) || null,
                        lastSavedEditor: hasVersionHistory ? lastVersion?.editorName || null : null,
                        lastSavedAtFormatted:
                            hasVersionHistory && lesson?.formatDate
                                ? lesson.formatDate(lastVersion?.updated_at) || null
                                : null,
                    };
                },

                /**
                 * Compute frame instructions for the current state.
                 * Mirrors logic from AppHeaderViewModel.frameInstructions getter.
                 */
                _computeFrameInstructions() {
                    // Use try-catch because base PlayerViewModel throws when accessing lesson property
                    let lesson = null;
                    try {
                        lesson = this.lesson;
                    } catch {
                        // Base PlayerViewModel throws when lesson is accessed
                    }

                    if (!lesson) return null;

                    if (this.showStartScreen) {
                        return lesson.test ? 'BEGIN TEST' : 'BEGIN LESSON';
                    }

                    if (this.showFinishScreen) {
                        return lesson.test ? 'TEST COMPLETE' : 'LESSON COMPLETE';
                    }

                    if (this.activeFrameViewModel?.complete) {
                        return 'SCREEN COMPLETE';
                    }

                    if (this.activeFrameViewModel?.frame) {
                        // Use try-catch because base Frame class throws when accessing miniInstructions
                        // Subclasses override this property, but in tests the base class may be used
                        try {
                            return this.activeFrameViewModel.frame.miniInstructions || null;
                        } catch {
                            return null;
                        }
                    }

                    return null;
                },

                /**
                 * Sync current player view model state to Redux.
                 * Called when linking and when relevant properties change.
                 */
                syncPlayerViewModelToRedux() {
                    try {
                        const data = this.getPlayerViewModelDataForSync();
                        appHeaderSynchronizer.syncPlayerViewModel(data);
                    } catch (e) {
                        console.warn('Failed to sync player view model to Redux:', e);
                    }
                },

                /**
                 * Update specific player view model properties in Redux.
                 * Called when individual properties change.
                 */
                updatePlayerViewModelInRedux(updates) {
                    try {
                        appHeaderSynchronizer.updatePlayerViewModel(updates);
                    } catch (e) {
                        console.warn('Failed to update player view model in Redux:', e);
                    }
                },

                _linkToLessonMessagingService(MessagingService) {
                    this.messagingServices.push(MessagingService);
                },

                _delinkFromMessagingService(MessagingService) {
                    Array.remove(this.messagingServices, MessagingService);
                },

                logInAllModes(eventType, extraPayload, options) {
                    return this.log(eventType, extraPayload, options, true);
                },

                log(eventType, extraPayload, options, logInAllModes) {
                    // Adding the suffixes is probably pointless at this point,
                    // since we don't log these events anymore.  But it's
                    // easy to make the change this way.

                    let logEvent = true;
                    logInAllModes = logInAllModes || false;
                    if (this.editorMode) {
                        eventType = `${eventType}:editor`;
                        logEvent = logInAllModes;
                    } else if (this.previewMode) {
                        eventType = `${eventType}:preview`;
                        logEvent = logInAllModes;
                    }

                    const payload = { ...this.logInfo(), ...extraPayload };
                    if (logEvent) {
                        return EventLogger.log(eventType, payload, options || {});
                    }
                    return new Event(eventType, payload);
                },

                clearClientStorage() {
                    this.runCallbacks('clear_client_storage', () => {});
                },

                launchBot() {
                    // implemented in FrameListPlayerViewModel
                    throw new Error('launchBot should be implemented by subclasses of playerviewModel');
                },

                hideBot() {
                    // implemented in FrameListPlayerViewModel
                    throw new Error('hideBot should be implemented by subclasses of playerviewModel');
                },

                _getBotContentInfo() {
                    // implemented in FrameListPlayerViewModel
                    throw new Error('_getBotContentInfo should be implemented by subclasses of playerviewModel');
                },
            };
        });
    },
]);
